export type Lang = 'en' | 'pt';

export interface Translations {
  brand: string;
  brandSub: string;
  nav: {
    location: string;
    listing: string;
    selling: string;
    customers: string;
    setup: string;
  };
  hero: {
    kicker: string;
    title: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
  };
  landing: {
    heroTitle: string;
    lede1: string;
    lede2: string;
    ctaCreate: string;
    ctaLocation: string;
    plate: {
      addressOnFile: string;
      verified: string;
      tierCountry: string;
      tierProvince: string;
      tierMunicipality: string;
      tierCity: string;
      tierSuburb: string;
      tierStreet: string;
      valCountry: string;
      valProvince: string;
      valMunicipality: string;
      valCity: string;
      valSuburb: string;
      valStreet: string;
      pinned: string;
      coords: string;
    };
  };
  profile: {
    kicker: string;
    title: string;
    note: string;
    rows: { title: string; desc: string }[];
  };
  location: {
    kicker: string;
    title: string;
    note: string;
    steps: { n: string; title: string; desc: string }[];
  };
  sell: {
    kicker: string;
    title: string;
    note: string;
    col1: { lead: string; desc: string }[];
    col2: { lead: string; desc: string }[];
  };
  customers: {
    kicker: string;
    title: string;
    note: string;
    rows: { title: string; desc: string }[];
  };
  team: {
    kicker: string;
    title: string;
    note: string;
    col1: { lead: string; desc: string }[];
    col2: { lead: string; desc: string }[];
  };
  setup: {
    kicker: string;
    title: string;
    note: string;
    steps: { n: string; title: string; desc: string }[];
  };
  close: {
    title: string;
    desc: string;
    ctaCreate: string;
    ctaTalk: string;
  };
  footer: {
    brand: string;
    poweredBy: string;
  };
}

export const TRANSLATIONS: Record<Lang, Translations> = {
  en: {
    brand: 'Temus',
    brandSub: 'Merchant',
    nav: {
      location: 'Location',
      listing: 'Your listing',
      selling: 'Selling',
      customers: 'Customers',
      setup: 'Setup',
    },
    hero: {
      kicker: 'Temus Merchant',
      title: 'Service Showcase',
      subtitle: 'Customers are already searching for what you sell, three streets away. Make sure the business they find is yours — right address, right hours, right number.',
      ctaPrimary: 'Get Started',
      ctaSecondary: 'Learn More',
    },
    landing: {
      heroTitle: 'Be findable, down to the street.',
      lede1: "Temus Merchant is where your business tells customers who it is, where it is, when it's open and what it sells — once, properly, in one place.",
      lede2: 'Most directories put you somewhere near your suburb and hope for the best. Temus puts you on your street, at your door.',
      ctaCreate: 'Create your listing',
      ctaLocation: 'See how location works',
      plate: {
        addressOnFile: 'Address on file',
        verified: 'Verified',
        tierCountry: 'Country',
        tierProvince: 'Province',
        tierMunicipality: 'Municipality',
        tierCity: 'City',
        tierSuburb: 'Suburb',
        tierStreet: 'Street',
        valCountry: 'South Africa',
        valProvince: 'Gauteng',
        valMunicipality: 'City of Tshwane',
        valCity: 'Pretoria',
        valSuburb: 'Hatfield',
        valStreet: 'Burnett Street',
        pinned: 'Pinned to coordinates',
        coords: '−25.7545, 28.2314',
      },
    },
    profile: {
      kicker: 'Your business profile',
      title: 'One profile, kept current, everywhere customers look.',
      note: 'Your details live in one record. Change your phone number once and it changes wherever your business appears — no chasing listings across the internet, no customers calling a line you disconnected two years ago.',
      rows: [
        { title: 'Who you are', desc: 'Registered name and trading name, registration and tax numbers, the year you opened, and the sectors and categories you actually work in.' },
        { title: 'How to reach you', desc: 'Phone, email and contact details in one place, so an enquiry never dies at a wrong number.' },
        { title: 'What you look like', desc: 'Upload your logo and a banner image. A listing with a face gets opened. A listing without one gets scrolled past.' },
        { title: "When you're open", desc: 'Set opening and closing times for each day of the week. Customers stop guessing, and you stop answering "are you open?" on WhatsApp at 8pm.' },
      ],
    },
    location: {
      kicker: 'Precise location',
      title: 'Your address, resolved properly — not guessed.',
      note: "Getting an address right in South Africa is genuinely hard. Suburb names repeat across provinces, municipal boundaries don't match what people call the area, and a pin dropped roughly in the right place sends a delivery driver to the wrong block. Temus resolves your address through the full hierarchy, one level at a time, and pins it to real coordinates.",
      steps: [
        { n: 'Step one', title: 'Confirm where you actually are', desc: 'Start from your GPS coordinates and confirm the point on the map is your front door, not the middle of the block.' },
        { n: 'Step two', title: 'Resolve the full address', desc: 'Country, province, municipality, city, suburb — each one narrowing the next. Then search your street by name and pick it from real street data.' },
        { n: 'Step three', title: 'Save it and move on', desc: "Choose your category and subcategories, save the location record, and it's locked in. Set it up once; you don't come back to it every month." },
      ],
    },
    sell: {
      kicker: 'Products, services and promotions',
      title: 'Show customers what you sell before they walk in.',
      note: 'A listing that only carries a name and a number makes people phone to ask basic questions. A listing that carries your catalogue answers them, and the people who call are the ones already close to buying.',
      col1: [
        { lead: 'Product showcase.', desc: 'Put your range in front of customers with images and detail, so they arrive knowing what you stock.' },
        { lead: 'Service showcase.', desc: 'List what you do and how you do it — the right fit for trades, salons, repairs, consultants and anyone who sells time rather than stock.' },
      ],
      col2: [
        { lead: 'Promotions.', desc: "Run a special and have it appear on your listing while it's live, instead of hoping people saw the poster in your window." },
        { lead: 'Advertising.', desc: 'Push your business in front of people searching your category and your area, not the whole country.' },
      ],
    },
    customers: {
      kicker: 'Enquiries and reputation',
      title: "Every enquiry lands somewhere you'll see it.",
      note: 'Leads that come in through email, WhatsApp, a form and a phone call end up scattered across four places and two people. Temus collects them in one inbox.',
      rows: [
        { title: 'Lead inbox', desc: "Incoming customer enquiries arrive in one list with the context attached, so you can see what's still waiting on a reply and what's already been handled. Nothing gets lost between a phone and an inbox." },
        { title: 'Reviews and ratings', desc: 'Read what customers are saying about you in the same place you manage everything else. Reviews you can see are reviews you can respond to.' },
        { title: 'Analytics and insights', desc: 'See how your listing is performing — what people are looking at and where your enquiries are coming from — so decisions about promotions and hours are based on something.' },
      ],
    },
    team: {
      kicker: 'Your team',
      title: 'Bring your staff in without handing over your password.',
      note: 'Invite the people who run your front desk, your catalogue or your enquiries, and give each of them their own access to the business. When someone leaves, you remove their access — not change every login you own.',
      col1: [
        { lead: 'Invite staff by email.', desc: 'Each person gets their own account tied to your business.' },
        { lead: 'Assign what they handle.', desc: "The person answering enquiries doesn't need to touch your registration details." },
      ],
      col2: [
        { lead: 'Settings and security.', desc: 'Manage your account, your access and your business details in one place.' },
        { lead: 'One business, many hands.', desc: "Your listing keeps running whether or not you're the one at the desk today." },
      ],
    },
    setup: {
      kicker: 'Getting started',
      title: 'Set up in an afternoon. Then leave it alone.',
      note: "This is not a system you have to feed. Most of the work is once-off, and after that you're only touching it when something about your business genuinely changes.",
      steps: [
        { n: 'First', title: 'Fill in your business', desc: "Name, contact details, registration numbers, what sector you're in and when you opened." },
        { n: 'Then', title: 'Pin your address', desc: 'Walk the three location steps once and your address is resolved down to your street.' },
        { n: 'Finally', title: 'Add hours and what you sell', desc: "Set your trading hours, upload your logo, load your products or services, and you're live." },
      ],
    },
    close: {
      title: 'Customers are already looking for what you sell, nearby.',
      desc: 'Make sure the business they find is yours, with the right address, the right hours and the right number attached to it.',
      ctaCreate: 'Create your listing',
      ctaTalk: 'Talk to us first',
    },
    footer: {
      brand: 'Temus Merchant',
      poweredBy: 'Location data powered by Larks Africa',
    },
  },

  pt: {
    brand: 'Temus',
    brandSub: 'Merchant',
    nav: {
      location: 'Localização',
      listing: 'O seu perfil',
      selling: 'Vendas',
      customers: 'Clientes',
      setup: 'Configuração',
    },
    hero: {
      kicker: 'Temus Merchant',
      title: 'Vitrine de Serviços',
      subtitle: 'Os clientes já estão à procura do que vende, a três quarteirões de distância. Certifique-se de que a empresa que encontram é a sua — morada certa, hora certa, número certo.',
      ctaPrimary: 'Começar',
      ctaSecondary: 'Saiba mais',
    },
    landing: {
      heroTitle: 'Fácil de encontrar, até à sua rua.',
      lede1: 'O Temus Merchant é o local onde o seu negócio diz aos clientes quem é, onde fica, quando está aberto e o que vende — uma única vez, correctamente, num só lugar.',
      lede2: 'A maioria dos directórios coloca-o algures perto do seu bairro e espera que corra bem. O Temus coloca-o na sua rua, à sua porta.',
      ctaCreate: 'Criar o seu perfil',
      ctaLocation: 'Ver como funciona a localização',
      plate: {
        addressOnFile: 'Endereço registado',
        verified: 'Verificado',
        tierCountry: 'País',
        tierProvince: 'Província',
        tierMunicipality: 'Município',
        tierCity: 'Cidade',
        tierSuburb: 'Bairro',
        tierStreet: 'Rua',
        valCountry: 'Angola',
        valProvince: 'Luanda',
        valMunicipality: 'Belas',
        valCity: 'Talatona',
        valSuburb: 'Patriota',
        valStreet: 'Rua Amílcar Cabral',
        pinned: 'Fixado nas coordenadas',
        coords: '−8.8383, 13.2343',
      },
    },
    profile: {
      kicker: 'O seu perfil de negócio',
      title: 'Um perfil, sempre actualizado, em todo o lado onde os clientes procuram.',
      note: 'Os seus dados vivem num único registo. Mude o seu número de telefone uma vez e ele muda onde quer que o seu negócio apareça — sem andar a corrigir perfis pela internet, sem clientes a ligar para um número que já desligou há dois anos.',
      rows: [
        { title: 'Quem é', desc: 'Nome registado e nome comercial, número de registo e número de identificação fiscal, o ano de abertura, e os sectores e categorias em que realmente trabalha.' },
        { title: 'Como o contactar', desc: 'Telefone, email e contactos num só lugar, para que uma pergunta de um cliente nunca se perca num número errado.' },
        { title: 'Como se apresenta', desc: 'Carregue o seu logótipo e uma imagem de destaque. Um perfil com rosto é aberto. Um perfil sem rosto é ignorado.' },
        { title: 'Quando está aberto', desc: 'Defina os horários de abertura e fecho para cada dia da semana. Os clientes deixam de adivinhar, e o senhor deixa de responder "estão abertos?" no WhatsApp às 20h.' },
      ],
    },
    location: {
      kicker: 'Localização precisa',
      title: 'O seu endereço, resolvido correctamente — não adivinhado.',
      note: 'Obter um endereço correcto em Angola é genuinamente difícil. Nomes de bairros repetem-se entre municípios, os limites administrativos nem sempre coincidem com o que as pessoas chamam à zona, e um alfinete colocado aproximadamente no local certo envia um estafeta para o quarteirão errado. O Temus resolve o seu endereço através de toda a hierarquia, um nível de cada vez, e fixa-o em coordenadas reais.',
      steps: [
        { n: 'Primeiro passo', title: 'Confirme onde realmente está', desc: 'Comece pelas suas coordenadas GPS e confirme que o ponto no mapa é a porta da sua loja, não o meio do quarteirão.' },
        { n: 'Segundo passo', title: 'Resolva o endereço completo', desc: 'País, província, município, cidade, bairro — cada um a restringir o seguinte. Depois procure a sua rua pelo nome e escolha-a a partir de dados reais.' },
        { n: 'Terceiro passo', title: 'Guarde e siga em frente', desc: 'Escolha a sua categoria e subcategorias, guarde o registo de localização, e fica definido. Configure uma vez; não precisa de voltar a isto todos os meses.' },
      ],
    },
    sell: {
      kicker: 'Produtos, serviços e promoções',
      title: 'Mostre aos clientes o que vende antes de eles entrarem.',
      note: 'Um perfil que só tem um nome e um número faz as pessoas ligarem para perguntar o básico. Um perfil com o seu catálogo já responde a essas perguntas, e quem liga já está perto de comprar.',
      col1: [
        { lead: 'Vitrine de produtos.', desc: 'Mostre a sua gama aos clientes com imagens e detalhe, para que cheguem já a saber o que tem disponível.' },
        { lead: 'Vitrine de serviços.', desc: 'Liste o que faz e como faz — ideal para técnicos, salões, reparações, consultores e qualquer negócio que vende tempo em vez de produtos.' },
      ],
      col2: [
        { lead: 'Promoções.', desc: 'Lance uma promoção e deixe-a aparecer no seu perfil enquanto estiver activa, em vez de esperar que alguém veja o cartaz na sua montra.' },
        { lead: 'Publicidade.', desc: 'Coloque o seu negócio à frente de quem procura a sua categoria e a sua zona, não o país inteiro.' },
      ],
    },
    customers: {
      kicker: 'Pedidos e reputação',
      title: 'Cada pedido cai num sítio onde o vai ver.',
      note: 'Pedidos que chegam por email, WhatsApp, um formulário e uma chamada acabam espalhados por quatro sítios e duas pessoas. O Temus reúne tudo numa só caixa de entrada.',
      rows: [
        { title: 'Caixa de pedidos', desc: 'Os pedidos dos clientes chegam numa só lista com o contexto anexado, para que veja o que ainda espera resposta e o que já foi tratado. Nada se perde entre um telefone e uma caixa de entrada.' },
        { title: 'Avaliações e classificações', desc: 'Leia o que os clientes dizem sobre si no mesmo sítio onde gere tudo o resto. Avaliações que consegue ver são avaliações a que consegue responder.' },
        { title: 'Análises e estatísticas', desc: 'Veja como o seu perfil está a ter desempenho — o que as pessoas estão a ver e de onde vêm os seus pedidos — para que as decisões sobre promoções e horários sejam baseadas em algo real.' },
      ],
    },
    team: {
      kicker: 'A sua equipa',
      title: 'Traga a sua equipa sem lhes entregar a sua palavra-passe.',
      note: 'Convide as pessoas que gerem a sua recepção, o seu catálogo ou os seus pedidos, e dê a cada uma o seu próprio acesso ao negócio. Quando alguém sai, remove o acesso dessa pessoa — não muda todos os logins que possui.',
      col1: [
        { lead: 'Convide a equipa por email.', desc: 'Cada pessoa recebe a sua própria conta ligada ao seu negócio.' },
        { lead: 'Atribua o que cada um trata.', desc: 'A pessoa que responde aos pedidos não precisa de mexer nos seus dados de registo.' },
      ],
      col2: [
        { lead: 'Definições e segurança.', desc: 'Faça a gestão da sua conta, do seu acesso e dos dados do seu negócio num só lugar.' },
        { lead: 'Um negócio, várias mãos.', desc: 'O seu perfil continua a funcionar quer esteja ou não à secretária hoje.' },
      ],
    },
    setup: {
      kicker: 'Como começar',
      title: 'Configure numa tarde. Depois deixe estar.',
      note: 'Isto não é um sistema que precise de alimentar constantemente. A maior parte do trabalho é feita uma única vez, e depois só volta a mexer quando algo no seu negócio realmente mudar.',
      steps: [
        { n: 'Primeiro', title: 'Preencha os dados do seu negócio', desc: 'Nome, contactos, números de registo, o sector em que está e quando abriu.' },
        { n: 'Depois', title: 'Fixe o seu endereço', desc: 'Percorra os três passos de localização uma vez e o seu endereço fica resolvido até à sua rua.' },
        { n: 'Por fim', title: 'Adicione horários e o que vende', desc: 'Defina os seus horários, carregue o seu logótipo, carregue os seus produtos ou serviços, e já está no ar.' },
      ],
    },
    close: {
      title: 'Os clientes já andam à procura do que vende, perto de si.',
      desc: 'Garanta que o negócio que encontram é o seu, com o endereço certo, o horário certo e o número certo associado a ele.',
      ctaCreate: 'Criar o seu perfil',
      ctaTalk: 'Fale connosco primeiro',
    },
    footer: {
      brand: 'Temus Merchant',
      poweredBy: 'Dados de localização fornecidos pela Larks Africa',
    },
  },
};
